/**
 * World configuration interface. Injected at runtime — not hardcoded.
 * Can come from a server, a save file, or a lobby setting.
 */
export interface WorldConfig {
  /** Hex radius in world units. */
  hexRadius: number;

  /** Hexes per cluster (width × height). */
  clusterWidth: number;
  clusterHeight: number;

  /** World grid dimensions in clusters (NOT hexes). */
  worldCols: number;
  worldRows: number;

  /** Max clusters kept in memory (LRU eviction beyond this). */
  maxActiveClusters: number;

  /** Seed for deterministic procedural generation. */
  seed: number;
}

/** Computed read-only properties derived from a WorldConfig. */
export interface WorldInfo {
  readonly config: WorldConfig;
  readonly totalClusters: number;
  readonly totalHexes: number;
  readonly worldWidthHexes: number;
  readonly worldHeightHexes: number;
}

/** Create a WorldInfo from a config. Single place for all derived values. */
export function createWorldInfo(config: WorldConfig): WorldInfo {
  const worldWidthHexes = config.worldCols * config.clusterWidth;
  const worldHeightHexes = config.worldRows * config.clusterHeight;

  return {
    config,
    totalClusters: config.worldCols * config.worldRows,
    totalHexes: worldWidthHexes * worldHeightHexes,
    worldWidthHexes,
    worldHeightHexes,
  };
}

/**
 * Preset configs for different server sizes.
 * The server decides which one to use — the client just receives it.
 */
export const WorldPresets = {
  /** Dev/test: 4×4 = 16 clusters, 40k hexes */
  tiny: (): WorldConfig => ({
    hexRadius: 2,
    clusterWidth: 50,
    clusterHeight: 50,
    worldCols: 4,
    worldRows: 4,
    maxActiveClusters: 16,
    seed: 42,
  }),

  /** Small server: 20×20 = 400 clusters, 1M hexes */
  small: (): WorldConfig => ({
    hexRadius: 2,
    clusterWidth: 50,
    clusterHeight: 50,
    worldCols: 20,
    worldRows: 20,
    maxActiveClusters: 32,
    seed: 42,
  }),

  /** Medium server: 50×50 = 2.500 clusters, 6.25M hexes */
  medium: (): WorldConfig => ({
    hexRadius: 2,
    clusterWidth: 50,
    clusterHeight: 50,
    worldCols: 50,
    worldRows: 50,
    maxActiveClusters: 32,
    seed: 42,
  }),

  /** Large server: 94×94 = 8.836 clusters, ~22M hexes */
  large: (): WorldConfig => ({
    hexRadius: 2,
    clusterWidth: 50,
    clusterHeight: 50,
    worldCols: 94,
    worldRows: 94,
    maxActiveClusters: 32,
    seed: 42,
  }),
} as const;
